// The canonical replay session (REP-1/3). A `ReplaySession` owns one pure `sim/`
// World plus a player body, and advances both by exactly one deterministic tick per
// `InputFrame`. It is the SINGLE step both sides of a replay run through — the
// recorder drives a session forward (capturing the frames + periodic hashes) and the
// player rebuilds a fresh session and re-feeds those frames — so re-simulation is
// bit-identical by construction (the M26 `hashWorld` isomorphism guarantee applied
// to an input stream). Pure: no DOM, no three, no transport.
//
// Per-tick order (fixed so record and replay agree): apply this frame's block edits,
// step the player from its movement input, then advance the world (the same shared
// `runTick`+fluids step the server host runs). Commands are carried in the format for
// completeness but not executed here (the canonical session covers movement + block
// edits + world evolution — see docs/decisions.md M33 for the boundary).
import type { Registries } from '../registry/load';
import { World } from '../engine/world';
import { runTick } from '../engine/tick';
import { hashWorld } from '../engine/worldhash';
import { makeBody, stepPlayer, type Body } from '../engine/physics';
import { buttonsToMoveInput, RepFlag, type InputFrame, type ReplayOptions, type Snapshot } from './format';

export interface SessionInit {
  seed: string;
  options: ReplayOptions;
  /** Player start pose when there is no `snapshot`. */
  spawn: { x: number; y: number; z: number; yaw: number; pitch: number };
  /** Start from a captured mid-world state instead of a fresh {seed, options} world. */
  snapshot?: Snapshot;
}

/** One deterministic world tick over `{world, player}`, shared by both sides of a
 *  replay. Kept as a free function so the recorder and player can never diverge. */
export function stepSessionWorld(world: World): void {
  runTick(world, { onFluids: () => { world.tickFluids(); } });
}

export class ReplaySession {
  private constructor(
    readonly world: World,
    readonly body: Body,
    public yaw: number,
    public pitch: number,
  ) {}

  /** Build a session from a fresh {seed, options} world (or a captured snapshot). */
  static create(reg: Registries, init: SessionInit): ReplaySession {
    const world = new World(reg, init.seed, {
      flat: init.options.flat,
      dimension: init.options.dimension,
      ...(init.options.flat && init.options.flatPreset ? { flatPreset: presetBlocks(init.options.flatPreset) } : {}),
    });
    if (init.snapshot) {
      applySnapshot(world, init.snapshot);
      const p = init.snapshot.player;
      const body = makeBody(p.x, p.y, p.z);
      body.vx = p.vx; body.vy = p.vy; body.vz = p.vz;
      return new ReplaySession(world, body, p.yaw, p.pitch);
    }
    const body = makeBody(init.spawn.x, init.spawn.y, init.spawn.z);
    // Touch the spawn column so the player has ground under it from tick 0.
    world.getChunk(Math.floor(init.spawn.x / 16), Math.floor(init.spawn.z / 16));
    return new ReplaySession(world, body, init.spawn.yaw, init.spawn.pitch);
  }

  /** Advance exactly one tick from a consumed input frame. */
  step(frame: InputFrame): void {
    // 1. The player's block edits this tick (place/break), applied like the net host.
    for (const e of frame.edits) {
      this.world.setBlockState(e.x, e.y, e.z, e.state);
      this.world.relightAfterEdit(e.x, e.y, e.z);
      this.world.scheduleFluidAround(e.x, e.y, e.z);
    }
    // 2. Player physics from movement input (against the post-edit world).
    this.yaw = frame.yaw;
    this.pitch = frame.pitch;
    const boxesAt = (x: number, y: number, z: number) => this.world.collisionBoxesAt(x, y, z);
    stepPlayer(this.body, buttonsToMoveInput(frame.buttons), frame.yaw, boxesAt, false, {
      flying: (frame.flags & RepFlag.Flying) !== 0,
      noClip: (frame.flags & RepFlag.NoClip) !== 0,
    });
    // 3. Advance the world (fluids, scheduled ticks, time, weather) — the shared step.
    stepSessionWorld(this.world);
  }

  /** The world-state fingerprint at this tick — the keyframe/drift anchor. */
  hash(): string {
    return hashWorld(this.world);
  }

  /** Current player pose (recorder snapshot + tests). */
  playerPose(): Snapshot['player'] {
    const b = this.body;
    return { x: b.x, y: b.y, z: b.z, vx: b.vx, vy: b.vy, vz: b.vz, yaw: this.yaw, pitch: this.pitch };
  }

  /** Capture the current state as a start snapshot (REP-1 mid-world enable): the
   *  clock, weather, every edited section, and the player pose. */
  snapshot(): Snapshot {
    const w = this.world;
    return {
      tickCount: w.tickCount,
      dayTime: w.dayTime,
      weather: {
        raining: w.weather.raining, thundering: w.weather.thundering,
        rainCountdown: w.weather.rainCountdown, thunderCountdown: w.weather.thunderCountdown,
      },
      player: this.playerPose(),
      // Every resident chunk's COORDS (not data): `hashWorld` folds all loaded chunks,
      // so a restart must reload the same set; unedited chunks regenerate from seed.
      loadedChunks: [...w.loadedChunks()].map((c) => ({ cx: c.cx, cz: c.cz })),
      sections: w.getDirtySections().map((s) => ({
        cx: s.cx, cz: s.cz, sy: s.sy, states: s.states ? Array.from(s.states) : null,
      })),
    };
  }
}

/** Restore a captured world state (clock + resident chunks + edited sections) into a
 *  fresh world so its `hashWorld` matches the snapshot's origin exactly. */
function applySnapshot(world: World, snap: Snapshot): void {
  world.tickCount = snap.tickCount;
  world.dayTime = snap.dayTime;
  Object.assign(world.weather, snap.weather);
  // Reload every resident chunk (regenerated from seed) so the loaded set matches.
  for (const c of snap.loadedChunks) world.getChunk(c.cx, c.cz);
  const touched = new Set<string>();
  for (const s of snap.sections) {
    world.overlaySection(s.cx, s.cz, s.sy, s.states ? Uint16Array.from(s.states) : null);
    touched.add(`${s.cx},${s.cz}`);
  }
  for (const key of touched) {
    const [cx, cz] = key.split(',').map(Number) as [number, number];
    world.recomputeChunkHeightmaps(cx, cz);
    world.relightChunk(cx, cz);
  }
}

/** Map a flat-preset id to the surface/filler/base remap the World expects (matches
 *  the server host's `presetBlocks`). */
function presetBlocks(id: string): { top: string; filler: string; base: string } {
  switch (id) {
    case 'desert': return { top: 'sand', filler: 'sand', base: 'sandstone' };
    case 'snowy': return { top: 'snow_block', filler: 'dirt', base: 'stone' };
    case 'stone': return { top: 'stone', filler: 'stone', base: 'stone' };
    default: return { top: 'grass_block', filler: 'dirt', base: 'stone' };
  }
}
